import * as Icons from "lucide-react";

/**
 * Friendly soft-loading screen shown on public dashboards / share pages /
 * seller portals when the data fetch hasn't completed (cold-start, network
 * blip, or — rarely — a revoked token).
 *
 * Designed for realtors, sellers, and buyers — so wording is intentionally
 * positive and frames the state as "fresh content is being prepared",
 * never as "the system is broken".  The same component handles both the
 * transient and not-found cases; we deliberately don't surface the
 * difference to the viewer (it's an internal distinction; either way the
 * right user action is the same — wait a beat and try again).
 */
export default function PublicLinkError({
  kind = "transient", // kept for telemetry/testid only — wording is identical
  onRetry,
  retrying = false,
  testId = "public-link-error",
  context = "your dashboard",
}) {
  const headline = retrying ? "Fetching the latest…" : "Fresh content is on the way";
  const subtitle = retrying
    ? "Pulling the newest reels and view stats — just a moment."
    : `We're preparing ${context} with the latest reels and view stats. Tap refresh in a moment to see the update.`;

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center text-center px-6">
      <div className="max-w-md" data-testid={testId}>
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-zinc-900 border border-white/10 mb-5">
          <Icons.Sparkles
            size={24}
            className={retrying ? "text-amber-300 animate-pulse" : "text-amber-300"}
          />
        </div>
        <h1 className="text-white text-lg font-semibold mb-2">{headline}</h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-6">{subtitle}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            disabled={retrying}
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed font-medium rounded-full px-5 py-2.5 text-sm transition"
            data-testid={`${testId}-retry`}
          >
            {retrying ? (
              <>
                <Icons.Loader2 size={14} className="animate-spin" />
                Refreshing…
              </>
            ) : (
              <>
                <Icons.RefreshCw size={14} />
                Refresh
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
